// Registrar horas trabajadas en un proyecto o un caso (a mano o al detener el cronometro). Tambien el boton del
// cronometro que va en la barra superior mientras corre.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { bolsaDelCaso, descontarDeBolsa, horarioSugerido, HORARIOS, MINIMO_MIN, minutosACobrar, movimientos, nombrePlan, RECARGO, saldoDe, type Horario } from '../api/bolsas';
import { fases, horasTexto, proyectos, registrarHoras } from '../api/proyectos';
import { useSession } from '../auth/session';
import { detenerCronometro, useCronometro } from '../lib/cronometro';
import { Icon } from '../lib/icons';
import { Dialog } from './Dialog';
import { SelectorFecha } from './SelectorFecha';
import { abrirDialogo } from './forms';
import { toast } from './toasts';

export const useProyectos = () => useQuery({ queryKey: ['proyectos'], queryFn: proyectos, staleTime: 60_000 });
/** Fases de todos los proyectos (D96). */
export const useFases = () => useQuery({ queryKey: ['fases'], queryFn: () => fases(), staleTime: 60_000 });

export function HorasForm({ minutos, proyectoId, casoId, descripcion, faseId, fecha: fechaInicial, onClose }: { minutos?: number; proyectoId?: string | null; casoId?: string | null; descripcion?: string; faseId?: string | null; fecha?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { yo } = useSession();
  const pr = useProyectos();
  const fs = useFases();
  const hoy = new Date();
  const [v, setV] = useState({
    horas: String(Math.floor((minutos ?? 60) / 60)),
    min: String((minutos ?? 60) % 60),
    fecha: fechaInicial ?? `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`,
    descripcion: descripcion ?? '',
    proyectoId: proyectoId ?? '',
    faseId: faseId ?? '',
    facturable: true,
  });
  const [guardando, setGuardando] = useState(false);
  const total = Number(v.horas || 0) * 60 + Number(v.min || 0);
  const fasesDel = v.proyectoId ? (fs.data ?? []).filter((x) => x.proyectoId === v.proyectoId) : [];
  // Bolsa del cliente del caso (D94): se muestra el horario y cuanto se va a descontar antes de guardar.
  const [horario, setHorario] = useState<Horario>(() => horarioSugerido());
  const fechaIso = new Date(`${v.fecha}T12:00:00`).toISOString();
  const bc = useQuery({
    queryKey: ['bolsas', 'del-caso', casoId, v.fecha],
    queryFn: async () => {
      const r = await bolsaDelCaso(casoId!, fechaIso);
      return r.bolsa ? { bolsa: r.bolsa, saldo: saldoDe(await movimientos(r.bolsa.id), r.bolsa) } : null;
    },
    enabled: !!casoId,
    staleTime: 30_000,
  });
  const conBolsa = v.facturable && bc.data ? bc.data : null;
  const descuento = minutosACobrar(total, horario);
  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    if (total <= 0) return toast('Escribe cuánto tiempo trabajaste.', 'info');
    setGuardando(true);
    try {
      const fecha = new Date(`${v.fecha}T12:00:00`).toISOString();
      const r = await registrarHoras({ descripcion: v.descripcion, minutos: total, fecha, miembroId: yo.miembroId ?? null, facturable: v.facturable, proyectoId: v.proyectoId || null, casoId: casoId ?? null, faseId: (v.proyectoId && v.faseId) || null });
      void qc.invalidateQueries({ queryKey: ['horas'] });
      // Bolsa de horas del cliente (D82): si el caso es de una empresa con bolsa vigente, se descuenta sola.
      const b = await descontarDeBolsa({ registroId: r.id, casoId: casoId ?? null, facturable: v.facturable, minutos: total, fecha, descripcion: v.descripcion, quien: yo.nombre, horario }).catch(() => null);
      if (b) void qc.invalidateQueries({ queryKey: ['bolsas'] });
      toast(b ? `Horas registradas y descontadas de la bolsa de ${b.bolsa.cliente}: quedan ${horasTexto(Math.max(0, b.saldo.restantes))}.` : 'Horas registradas.', b?.saldo.semaforo === 'rojo' ? 'error' : undefined);
      onClose();
    } catch (err) {
      toast((err as Error).message, 'error');
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Dialog titulo="Registrar horas" onClose={onClose} width={560}>
      <form className="form" onSubmit={guardar}>
        <label>
          ¿Qué hiciste?
          <input id="h-desc" value={v.descripcion} onChange={(e) => setV({ ...v, descripcion: e.target.value })} required placeholder="Configuración de troncal, reunión de avance…" />
        </label>
        <div className="grid3" style={{ gridTemplateColumns: '90px 90px minmax(0,1fr)' }}>
          <label>
            Horas
            <input id="h-h" type="number" min={0} max={24} value={v.horas} onChange={(e) => setV({ ...v, horas: e.target.value })} />
          </label>
          <label>
            Minutos
            <input id="h-m" type="number" min={0} max={59} value={v.min} onChange={(e) => setV({ ...v, min: e.target.value })} />
          </label>
          <label>
            Día
            <SelectorFecha id="h-f" value={v.fecha} onChange={(x) => setV({ ...v, fecha: x })} required etiqueta="Día trabajado" />
          </label>
        </div>
        <label>
          Proyecto
          <select id="h-p" value={v.proyectoId} onChange={(e) => setV({ ...v, proyectoId: e.target.value, faseId: '' })}>
            <option value="">{casoId ? 'Solo el caso' : 'Sin proyecto'}</option>
            {(pr.data ?? [])
              .filter((p) => p.estado !== 'TERMINADO' || p.id === v.proyectoId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                  {p.empresa ? ` · ${p.empresa.nombre}` : ''}
                </option>
              ))}
          </select>
        </label>
        {fasesDel.length ? (
          <label>
            Fase
            <select id="h-fase" value={v.faseId} onChange={(e) => setV({ ...v, faseId: e.target.value })}>
              <option value="">Sin fase</option>
              {fasesDel.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.nombre}
                  {x.estado === 'HECHA' ? ' (hecha)' : ''}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="chk">
          <input type="checkbox" checked={v.facturable} onChange={(e) => setV({ ...v, facturable: e.target.checked })} /> Facturable al cliente
        </label>
        {conBolsa ? (
          <div className="horas-bolsa">
            <span className="muted">
              Horario del servicio · bolsa {nombrePlan(conBolsa.bolsa.plan)} de {conBolsa.bolsa.cliente}
            </span>
            <div className="seg" role="group" aria-label="Horario del servicio">
              {HORARIOS.map(([h, t, d]) => (
                <button type="button" key={h} aria-pressed={horario === h} onClick={() => setHorario(h)} title={d}>
                  {t}
                </button>
              ))}
            </div>
            <p className="horas-calc" aria-live="polite">
              Se descontarán <b>{horasTexto(descuento)}</b>
              {total > 0 && (total < MINIMO_MIN || horario !== 'NORMAL') ? (
                <span className="muted">
                  {' '}
                  ({[total < MINIMO_MIN ? `mínimo ${MINIMO_MIN} min` : '', horario !== 'NORMAL' ? `+${Math.round((RECARGO[horario] - 1) * 100)} %` : ''].filter(Boolean).join(' · ')})
                </span>
              ) : null}
              {' · '}
              {conBolsa.saldo.restantes - descuento >= 0 ? (
                <span className="muted">
                  quedan {horasTexto(conBolsa.saldo.restantes - descuento)}
                  {conBolsa.bolsa.plan === 'SUPPORT_12' ? ' este mes' : ''}
                </span>
              ) : (
                <span className="bad-ink">faltan {horasTexto(descuento - Math.max(0, conBolsa.saldo.restantes))}: la bolsa queda en rojo</span>
              )}
            </p>
          </div>
        ) : null}
        <div className="actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn primary" disabled={guardando}>
            {guardando ? 'Guardando…' : 'Registrar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// Pastilla del cronometro en la barra superior mientras corre.
export function CronometroBarra() {
  const c = useCronometro();
  const [, tic] = useState(0);
  useEffect(() => {
    if (!c) return;
    // Cada segundo: antes se veia "0:01" un minuto entero y parecia que no andaba.
    const t = setInterval(() => tic((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [c]);
  if (!c) return null;
  const seg = Math.max(0, Math.floor((Date.now() - c.inicio) / 1000));
  const reloj = `${Math.floor(seg / 3600)}:${String(Math.floor(seg / 60) % 60).padStart(2, '0')}:${String(seg % 60).padStart(2, '0')}`;
  const detener = () => {
    const r = detenerCronometro();
    if (r) abrirDialogo({ tipo: 'horas', minutos: r.minutos, proyectoId: r.proyectoId ?? undefined, casoId: r.casoId ?? undefined, descripcion: r.etiqueta });
  };
  return (
    <button className="btn crono" onClick={detener} title="Detener y registrar las horas">
      <span className="crono-punto" /> {reloj} · {c.etiqueta.slice(0, 28)} <Icon name="pause" />
    </button>
  );
}
